/**
 * Сервис для маппинга статусов и ролей между Frontend и Backend
 */
export class MappingService {
    /**
     * Конвертирует Frontend статус в Backend статус
     */
    static mapStatusToBackend(frontendStatus) {
        return this.FE_TO_BE_STATUS_MAP[frontendStatus];
    }
    /**
     * Конвертирует Backend заказ в Frontend статус (1:1 mapping)
     */
    static mapStatusToFrontend(order) {
        return this.mapStatusToFrontendSimple(order.status);
    }
    /**
     * Конвертирует Backend статус в Frontend статус (1:1 mapping)
     */
    static mapStatusToFrontendSimple(backendStatus) {
        const statusMap = {
            'PENDING_PAYMENT': 'pending-payment',
            'PENDING': 'pending',
            'CONFIRMED': 'confirmed',
            'PREPARING': 'preparing',
            'READY': 'ready',
            'IN_TRANSIT': 'in-transit',
            'DELIVERED': 'delivered',
            'CANCELLED': 'cancelled',
            'SCHEDULED': 'scheduled'
        };
        return statusMap[backendStatus] || 'pending';
    }
    /**
     * Конвертирует Frontend роль в Backend роль с флагами
     */
    static mapRoleToBackend(frontendRole) {
        return this.FE_TO_BE_ROLE_MAP[frontendRole];
    }
    static mapFloristConfirmationStatusToBackend(frontendStatus) {
        return this.FE_TO_BE_FLORIST_CONFIRMATION_MAP[frontendStatus];
    }
    static mapFloristConfirmationStatusToFrontend(backendStatus) {
        const statusMap = {
            PENDING: 'pending',
            ACCEPTED: 'accepted',
            COMPLETED: 'completed',
            DECLINED: 'declined'
        };
        return statusMap[backendStatus];
    }
    static mapChatStatusToBackend(frontendStatus) {
        return this.FE_TO_BE_CHAT_STATUS_MAP[frontendStatus];
    }
    static mapChatStatusToFrontend(backendStatus) {
        const statusMap = {
            NEW: 'new',
            OPEN: 'open',
            PENDING: 'pending',
            RESOLVED: 'resolved',
            CLOSED: 'closed'
        };
        return statusMap[backendStatus];
    }
    /**
     * Конвертирует Backend пользователя в Frontend роль.
     * Throws on unknown role (fail-closed — no silent fallback).
     */
    static mapRoleToFrontend(user) {
        // Legacy fallback: ADMIN + flags → 'dispatcher' (for unmigrated tokens)
        if (user.role === 'ADMIN' && (user.isFloristManager || user.isDeliveryManager)) {
            return 'dispatcher';
        }
        const roleMap = {
            'ADMIN': 'admin',
            'DISPATCHER': 'dispatcher',
            'FLORIST': 'florist',
            'COURIER': 'courier',
            'SUPPORT': 'support',
            'CUSTOMER': 'customer',
        };
        const mapped = roleMap[user.role];
        if (!mapped) {
            throw new Error(`Unknown backend role: ${user.role}`);
        }
        return mapped;
    }
    /**
     * Получает список доступных переходов статусов для роли
     */
    static getAvailableStatusTransitions(currentStatus, userRole) {
        const transitions = {
            'pending-payment': {
                'admin': ['pending', 'cancelled'],
                'dispatcher': [],
                'manager-florist': [],
                'florist': [],
                'delivery-manager': [],
                'courier': [],
                'support': [],
                'customer': ['cancelled']
            },
            'pending': {
                'admin': ['confirmed', 'cancelled'],
                'dispatcher': ['confirmed', 'cancelled'],
                'manager-florist': ['confirmed', 'cancelled'],
                'florist': [],
                'delivery-manager': [],
                'courier': [],
                'support': [],
                'customer': ['cancelled']
            },
            'confirmed': {
                'admin': ['pending', 'preparing', 'cancelled'],
                'dispatcher': ['pending', 'preparing', 'cancelled'],
                'manager-florist': ['pending', 'preparing', 'cancelled'],
                'florist': ['preparing', 'cancelled'],
                'delivery-manager': [],
                'courier': [],
                'support': [],
                'customer': []
            },
            'preparing': {
                'admin': ['ready', 'cancelled'],
                'dispatcher': ['ready', 'cancelled'],
                'manager-florist': ['ready', 'cancelled'],
                'florist': ['ready', 'cancelled'],
                'delivery-manager': [],
                'courier': [],
                'support': [],
                'customer': []
            },
            'ready': {
                'admin': ['in-transit', 'cancelled'],
                'dispatcher': ['in-transit', 'cancelled'],
                'manager-florist': [],
                'florist': [],
                'delivery-manager': ['in-transit', 'cancelled'],
                'courier': ['in-transit', 'cancelled'],
                'support': [],
                'customer': []
            },
            'in-transit': {
                'admin': ['delivered', 'cancelled'],
                'dispatcher': ['delivered', 'cancelled'],
                'manager-florist': [],
                'florist': [],
                'delivery-manager': ['delivered', 'cancelled'],
                'courier': ['delivered', 'cancelled'],
                'support': [],
                'customer': []
            },
            'delivered': {
                'admin': [],
                'dispatcher': [],
                'manager-florist': [],
                'florist': [],
                'delivery-manager': [],
                'courier': [],
                'support': [],
                'customer': []
            },
            'cancelled': {
                'admin': [],
                'dispatcher': [],
                'manager-florist': [],
                'florist': [],
                'delivery-manager': [],
                'courier': [],
                'support': [],
                'customer': []
            },
            'scheduled': {
                'admin': ['pending', 'cancelled'],
                'dispatcher': ['pending', 'cancelled'],
                'manager-florist': ['pending', 'cancelled'],
                'florist': [],
                'delivery-manager': [],
                'courier': [],
                'support': [],
                'customer': ['cancelled']
            }
        };
        return transitions[currentStatus]?.[userRole] || [];
    }
    /**
     * Проверяет, может ли пользователь с данной ролью изменить статус
     */
    static canChangeStatus(fromStatus, toStatus, userRole) {
        const availableTransitions = this.getAvailableStatusTransitions(fromStatus, userRole);
        return availableTransitions.includes(toStatus);
    }
}
// Маппинг Frontend статусов в Backend (1:1 mapping)
MappingService.FE_TO_BE_STATUS_MAP = {
    'pending-payment': 'PENDING_PAYMENT',
    'pending': 'PENDING',
    'confirmed': 'CONFIRMED',
    'preparing': 'PREPARING',
    'ready': 'READY',
    'in-transit': 'IN_TRANSIT',
    'delivered': 'DELIVERED',
    'cancelled': 'CANCELLED',
    'scheduled': 'SCHEDULED'
};
// Маппинг Frontend ролей в Backend
MappingService.FE_TO_BE_ROLE_MAP = {
    'admin': { role: 'ADMIN' },
    'dispatcher': { role: 'DISPATCHER' },
    'florist': { role: 'FLORIST' },
    'courier': { role: 'COURIER' },
    'support': { role: 'SUPPORT' },
    'customer': { role: 'CUSTOMER' },
    // Legacy aliases (deprecated — resolve to DISPATCHER)
    'manager-florist': { role: 'DISPATCHER' },
    'delivery-manager': { role: 'DISPATCHER' },
};
MappingService.FE_TO_BE_FLORIST_CONFIRMATION_MAP = {
    pending: 'PENDING',
    accepted: 'ACCEPTED',
    completed: 'COMPLETED',
    declined: 'DECLINED'
};
MappingService.FE_TO_BE_CHAT_STATUS_MAP = {
    new: 'NEW',
    open: 'OPEN',
    pending: 'PENDING',
    resolved: 'RESOLVED',
    closed: 'CLOSED'
};
